import { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import useOfferDesign from 'api/useOfferDesign';
import useOffers from 'api/useOffers';
import { OfferUI } from 'common/contracts';
import { OfferType } from 'constants/enums';
import { EToasterStatus, showToast } from 'utils/showToast';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';

import DialogModal from 'components/DialogModal/DialogModal';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { Spinner } from 'components/ui/spinner';

const EditOfferDesignModal = ({
  offerId,
  offerDesignType,
  onClose,
  isOpen,
  offerType
}: {
  offerId: string;
  offerDesignType: OfferDesignTypes;
  onClose: () => void;
  isOpen: boolean;
  offerType: OfferType;
}) => {
  const { publisherId } = useParams();
  const { getOffers, getOffer, updateOfferDesign } = useOffers({
    offerId,
    offerType
  });
  const { getOfferDesigns } = useOfferDesign(publisherId);

  const isLoading = getOffer.isLoading || getOfferDesigns.isLoading;
  const [selectedOfferDesign, setSelectedOfferDesign] = useState<
    | {
        label: string;
        value: string;
        externalId: string;
      }
    | undefined
  >(undefined);

  const currentOfferDesign = useMemo(() => {
    if (getOffer.isLoading || !getOffer.data?.result) return undefined;

    return {
      label: getOffer.data.result.offerUi.name,
      value: getOffer.data.result.offerUi.offerUiId,
      externalId: getOffer.data.result.offerUi.externalId
    };
  }, [getOffer.isLoading, getOffer.data?.result]);

  const offerDesigns = useMemo(() => {
    if (getOfferDesigns.isLoading || !getOfferDesigns.data?.result) return [];

    return getOfferDesigns.data.result
      .filter((oi: OfferUI) => oi.offerUiType === offerDesignType)
      .map((offerUI: OfferUI) => ({
        label: offerUI.name,
        value: offerUI._id,
        externalId: offerUI.externalId
      }));
  }, [
    getOfferDesigns.isLoading,
    getOfferDesigns.data?.result,
    offerDesignType
  ]);

  useEffect(() => {
    if (currentOfferDesign && !selectedOfferDesign) {
      setSelectedOfferDesign(currentOfferDesign);
    }
  }, [currentOfferDesign, selectedOfferDesign]);

  const handleSave = () => {
    if (!selectedOfferDesign?.value) return;

    updateOfferDesign.mutate(
      {
        offerId,
        offerDesignId: selectedOfferDesign.value
      },
      {
        onSuccess: () => {
          showToast({
            message: 'Offer design updated successfully',
            status: EToasterStatus.SUCCESS
          });
          getOffers.refetch();
          onClose();
        },
        onError: () => {
          showToast({
            message: 'Error updating offer design',
            status: EToasterStatus.ERROR
          });
        }
      }
    );
  };

  return (
    <DialogModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Edit Offer"
      description="Customize the design of your offer"
      modalActions={{
        onConfirm: {
          label: 'Save',
          action: handleSave,
          disabled:
            isLoading ||
            selectedOfferDesign?.value === currentOfferDesign?.value
        },
        onClose: {
          label: 'Cancel',
          action: onClose,
          variant: 'link'
        }
      }}
    >
      {isLoading ? (
        <div className="w-full flex justify-center">
          <Spinner className="size-12" />
        </div>
      ) : (
        <InputSelectDropdown
          label="Offer Design"
          options={offerDesigns}
          defaultSelected={currentOfferDesign}
          onOptionChange={(value) => {
            const selected = offerDesigns.find(
              (option: any) => option.value === value
            );
            setSelectedOfferDesign(selected);
          }}
        />
      )}
    </DialogModal>
  );
};

export default EditOfferDesignModal;
